import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, shareReplay, catchError } from "rxjs/operators";
import { RestProviderService } from "src/app/services/rest-provider/rest-provider.service";
import { DynamicFieldsManagerService } from "src/app/services/dynamic-fields/dynamic-fields-manager.service";
import { TranslationsProviderService } from "src/app/services/translations-provider/translations-provider.service";
import { LocalStorageService } from "src/app/services/local-storage-service/local-storage.service";
import { RequestModel } from "src/app/models/request/request-model";
import { environment } from "src/environments/environment";

/** Shape of GET api/translation/config. */
interface TranslationConfig {
  Enabled: boolean;
  Languages: string[];
  Models: { [modelFqn: string]: string[] };
  SuggestEnabled: boolean;
}

/**
 * One coloured language pill per target language, sitting next to a translatable free-text field.
 * Clicking a pill opens an inline editor for that language's value.
 *
 * Renders NOTHING at all when the feature is off, when the field is not declared translatable, or
 * when the config call fails. Same rule the concept panel follows: an unrelated form must not grow
 * a broken-looking empty widget because a feature it never asked for is switched on somewhere.
 */
@Component({
  selector: "sem-translation-pills",
  templateUrl: "./sem-translation-pills.component.html",
  styleUrls: ["./sem-translation-pills.component.scss"],
  standalone: false,
})
export class SemTranslationPillsComponent implements OnInit, OnDestroy {
  /** .NET property name of the field these pills belong to, e.g. "ObjectTitle". */
  @Input() set fieldName(v: string) {
    // The generated templates write the binding either as name="ObjectTitle" or as
    // name="model.ObjectTitle"; the registry only ever knows the bare property name.
    this._fieldName = (v || "").replace(/^model\./, "");
  }
  get fieldName(): string {
    return this._fieldName;
  }
  private _fieldName: string = "";

  /** Set by the host control when the field is read-only, so translations follow field security. */
  @Input() disabled: boolean = false;

  /**
   * True on textarea-backed fields. An Opis translation is a paragraph, and editing a paragraph in a
   * one-line input is the single most irritating thing this control can do.
   */
  @Input() multiline: boolean = false;

  /**
   * Config is fetched ONCE per application load and shared by every pill on the form.
   * A document form carries a dozen text fields; a request each would be a dozen identical calls.
   */
  private static config$: Observable<TranslationConfig> = null;

  /** Global user preference: hide every pill without turning the feature off. */
  static pillsHidden: boolean = localStorage.getItem("translationPillsHidden") === "true";

  /**
   * The one popover open anywhere on the form. Two open popovers overlap into an unreadable stack,
   * so opening one closes the other — enforced across instances, because each field is its own.
   */
  private static openInstance: SemTranslationPillsComponent = null;

  /** Language code -> Slovene language name, the fallback when Intl has no answer. */
  private static readonly LANGUAGE_NAMES: { [code: string]: string } = {
    en: "Angleščina",
    de: "Nemščina",
    hr: "Hrvaščina",
    it: "Italijanščina",
    sl: "Slovenščina",
    sr: "Srbščina",
    fr: "Francoščina",
    es: "Španščina",
    hu: "Madžarščina",
  };

  /**
   * A preview longer than this turns the form into a wall of prose. Applied per pill and NEVER by
   * flex shrinking, so two pills side by side cut at the same place rather than whichever one the
   * layout happened to squeeze.
   */
  private static readonly PREVIEW_CHARS = 22;

  languages: string[] = [];
  openLanguage: string = null;

  /**
   * The editor writes HERE, not into the bound model. Everything the popover does is provisional
   * until it is committed, which is what gives Esc something to revert TO. Without this boundary the
   * input was bound straight through, so Esc could only ever close a change that had already landed.
   */
  draft: string = "";
  /** The value as it stood when the popover opened. Restored verbatim on cancel. */
  private draftOriginal: string = "";

  /**
   * Translations removed in this editing session but not yet saved, lang -> the value that was
   * removed. The pill stays on the row wearing a struck-through pending state, because a deletion
   * the user has not saved must not look identical to one the database has already accepted. Cleared
   * when the form goes pristine, i.e. when the save actually happened.
   */
  pendingRemovals: { [lang: string]: string } = {};
  /** Popover opens upward when there is no room for it below the field. */
  flipUp: boolean = false;
  /** Per FIELD and per visit: revealing the empty slots on one field says nothing about the rest. */
  showEmpty: boolean = false;
  /** Whether the installation has a translation model configured at all. */
  suggestAvailable: boolean = false;
  /** Language currently being machine-translated, so the button can say so. */
  suggesting: string = null;

  private cachedLabel: string = null;
  private cachedMaxLength: number = null;
  private cachedNames: { [code: string]: string } = {};

  constructor(
    private rest: RestProviderService,
    private dfms: DynamicFieldsManagerService,
    private i18n: TranslationsProviderService,
    private localStorage: LocalStorageService,
    private host: ElementRef
  ) {}

  get hidden(): boolean {
    return SemTranslationPillsComponent.pillsHidden;
  }

  ngOnInit(): void {
    if (!SemTranslationPillsComponent.config$) {
      SemTranslationPillsComponent.config$ = this.rest.getData<TranslationConfig>("api/translation/config").pipe(
        // A failed config call must leave the form exactly as it was, not blow up the field.
        catchError(() => of(<TranslationConfig>{ Enabled: false, Languages: [], Models: {}, SuggestEnabled: false })),
        shareReplay(1)
      );
    }

    SemTranslationPillsComponent.config$
      .pipe(
        map((cfg) => {
          if (!cfg || !cfg.Enabled || !this.fieldName) {
            return [];
          }

          this.suggestAvailable = cfg.SuggestEnabled === true;

          // Slice 1 declares Item only, and the frontend has no reliable handle on the record's
          // DOMAIN FQN (it holds an application model). Matching the field name across every
          // declaration is therefore the pragmatic test here. It cannot over-permit: the server
          // re-checks (model, field) against the same registry and drops anything undeclared.
          const declared = Object.keys(cfg.Models || {}).some(
            (fqn) => (cfg.Models[fqn] || []).indexOf(this.fieldName) >= 0
          );

          return declared ? cfg.Languages || [] : [];
        })
      )
      .subscribe((langs) => (this.languages = langs));
  }

  ngOnDestroy(): void {
    if (SemTranslationPillsComponent.openInstance === this) {
      SemTranslationPillsComponent.openInstance = null;
    }
  }

  // ---------------------------------------------------------------- localization

  /** Keys already reported missing, so one absent key warns once and not once per change detection. */
  private static warnedKeys: { [key: string]: boolean } = {};

  /**
   * Localized UI string, falling back to the Slovene default when the key is missing from the
   * payload. Same helper the spell checker uses; keys live in Domain/Translations/Translation.*.resx.
   *
   * The fallback stays SLOVENE in production: a curator must never be shown a raw resource key
   * because a deployment is half-finished. But a silent fallback is its own defect — under a
   * Croatian UI it renders Slovene and looks like a translation bug, and when the whole payload is
   * absent (a stale build, or the HR api/translation/all 500 that went unnoticed for months) it
   * hides a product-wide outage behind text that reads fine to a Slovene speaker. So every miss is
   * reported once to the console in all builds, and marked on screen in dev builds only.
   */
  t(key: string, fallback: string): string {
    const v = this.i18n.translate(key);
    // translate() hands back the raw key when it is not in the translations payload
    if (v && v !== key) {
      return v;
    }

    if (!SemTranslationPillsComponent.warnedKeys[key]) {
      SemTranslationPillsComponent.warnedKeys[key] = true;
      console.warn(
        "[i18n] missing translation key '" + key + "' for language '" + this.uiLanguage +
        "' — falling back to the Slovene default");
    }

    return environment.production ? fallback : "⟨" + fallback + "⟩";
  }

  /** Current UI language of the application, lowercase ISO code. */
  private get uiLanguage(): string {
    try {
      const raw = this.localStorage.get("language");
      const code = (raw || "").toString().replace(/"/g, "").trim();
      return code ? code.toLowerCase() : "sl";
    } catch {
      return "sl";
    }
  }

  /**
   * Language names come from Intl in the CURRENT UI language rather than from a resource key each.
   * A language name is standard reference data, not product copy: hardcoding nine of them per
   * culture is nine translations to keep and still wrong the moment a tenth language is configured.
   * Falls back to the Slovene table, then to the bare code.
   */
  languageName(lang: string): string {
    const code = (lang || "").toLowerCase();
    if (!code) {
      return "";
    }
    if (this.cachedNames[code]) {
      return this.cachedNames[code];
    }

    let name = "";
    try {
      const intl: any = (Intl as any).DisplayNames;
      if (intl) {
        const value = new intl([this.uiLanguage, "en"], { type: "language" }).of(code);
        // Intl returns the input unchanged for a code it does not know.
        name = value && value.toLowerCase() !== code ? value : "";
        if (name) {
          name = name.charAt(0).toUpperCase() + name.substring(1);
        }
      }
    } catch {
      name = "";
    }

    this.cachedNames[code] = name || SemTranslationPillsComponent.LANGUAGE_NAMES[code] || code.toUpperCase();
    return this.cachedNames[code];
  }

  // ---------------------------------------------------------------- model access

  /** The transient carrier the backend reads. Created lazily so an untouched record posts nothing. */
  private carrier(create: boolean): any[] {
    const root = this.dfms.globalModel;
    if (!root) {
      return null;
    }
    if (!root.Translations && create) {
      root.Translations = [];
    }
    return root.Translations || null;
  }

  private row(lang: string, create: boolean): any {
    const rows = this.carrier(create);
    if (!rows) {
      return null;
    }

    let found = rows.find(
      (r) => r && r.FieldName === this.fieldName && (r.LanguageCode || "").toLowerCase() === lang
    );

    if (!found && create) {
      // Value is "" and not undefined on purpose: clearEmpties() prunes any object that ends up
      // with zero keys before the POST, and a row with three keys survives it.
      found = { FieldName: this.fieldName, LanguageCode: lang, Value: "" };
      rows.push(found);
    }
    return found;
  }

  valueFor(lang: string): string {
    const r = this.row(lang, false);
    return r ? r.Value || "" : "";
  }

  hasValue(lang: string): boolean {
    return (this.valueFor(lang) || "").trim().length > 0;
  }

  /** True while this language is deleted locally but the record has not been saved yet. */
  isPendingRemoval(lang: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.pendingRemovals, lang);
  }

  /**
   * A save marks the form pristine, so a pristine form means nothing of ours is outstanding and the
   * pending marks have become the truth. Checked lazily off the render path rather than through a
   * save hook, because the control has no handle on the form's submit.
   */
  private syncPending(): void {
    if (!Object.keys(this.pendingRemovals).length) {
      return;
    }
    const form = this.dfms.globalForm ? this.dfms.globalForm.form : null;
    if (form && form.pristine) {
      this.pendingRemovals = {};
    }
  }

  /**
   * Languages drawn at rest: the filled ones, the one that is open, anything pending removal, plus
   * all of them when revealed.
   */
  get visibleLanguages(): string[] {
    this.syncPending();
    if (this.showEmpty) {
      return this.languages;
    }
    return this.languages.filter(
      (l) => this.hasValue(l) || this.openLanguage === l || this.isPendingRemoval(l)
    );
  }

  /** True when the globe has something to reveal — no point offering it on a fully translated field. */
  get hasHiddenLanguages(): boolean {
    return this.languages.length > this.visibleLanguages.length;
  }

  /** Truncated translation shown inside the pill, so the record reads without being clicked open. */
  preview(lang: string): string {
    const raw = this.isPendingRemoval(lang) ? this.pendingRemovals[lang] : this.valueFor(lang);
    const v = (raw || "").replace(/\s+/g, " ").trim();
    if (!v) {
      return "";
    }
    const cap = SemTranslationPillsComponent.PREVIEW_CHARS;
    return v.length > cap ? v.substring(0, cap - 1).trimEnd() + "…" : v;
  }

  /** Hover text: the full translation, or an invitation when the slot is empty. */
  tooltip(lang: string): string {
    const name = this.languageName(lang);
    if (this.isPendingRemoval(lang)) {
      return (
        name +
        " – " +
        this.t("galis_js_translation_pending_removal", "bo odstranjen ob shranjevanju; kliknite za razveljavitev")
      );
    }
    const v = (this.valueFor(lang) || "").trim();
    return v ? name + ": " + v : name + " – " + this.t("galis_js_translation_no_translation", "ni prevoda");
  }

  /**
   * Character limit of the FIELD being translated, read off the native control the host renders in
   * the same .form-group — the same DOM route fieldLabel takes. A translation must not be allowed to
   * be longer than the value it translates, and the limit belongs to the field, so hardcoding one
   * here would silently disagree with the form the moment anybody changed it in the designer.
   */
  get maxLength(): number {
    if (this.cachedMaxLength !== null) {
      return this.cachedMaxLength;
    }
    let n = 0;
    try {
      const group = (this.host.nativeElement as HTMLElement).closest(".form-group");
      const el = group ? group.querySelector("textarea[maxlength], input[maxlength]") : null;
      const raw = el ? el.getAttribute("maxlength") : null;
      const parsed = raw ? parseInt(raw, 10) : NaN;
      n = isNaN(parsed) || parsed <= 0 ? 0 : parsed;
    } catch {
      n = 0;
    }
    this.cachedMaxLength = n;
    return n;
  }

  /** True when there is no Slovene original to translate — stated out loud rather than left blank. */
  get sourceEmpty(): boolean {
    return (this.sourceValue || "").trim().length === 0;
  }

  placeholder(lang: string): string {
    return this.t("galis_js_translation_placeholder", "Prevod v {0}").replace(
      "{0}",
      this.languageName(lang).toLowerCase()
    );
  }

  /**
   * The Slovene original, so a curator is not asked to translate a string the popover is covering.
   * Read straight off the bound model — the same object the field itself edits.
   */
  get sourceValue(): string {
    const root = this.dfms.globalModel;
    const v = root && this.fieldName ? root[this.fieldName] : null;
    return typeof v === "string" ? v : v == null ? "" : String(v);
  }

  /**
   * Human field caption. The control itself only ever knows the .NET property name; the caption is
   * rendered by a sibling label inside the shared .form-group wrapper, so it is read from there.
   * A miss degrades to the property name, which is what was shown before.
   */
  get fieldLabel(): string {
    if (this.cachedLabel !== null) {
      return this.cachedLabel;
    }
    let text = "";
    try {
      const group = (this.host.nativeElement as HTMLElement).closest(".form-group");
      const label = group ? group.querySelector("label") : null;
      text = label ? (label.textContent || "").trim() : "";
    } catch {
      text = "";
    }
    this.cachedLabel = text || this.fieldName;
    return this.cachedLabel;
  }

  // ---------------------------------------------------------------- interaction

  toggle(lang: string): void {
    // A pill wearing the pending-removal state is an undo button, not an editor. Clicking the thing
    // that says "this is about to be deleted" has to be the way to take it back.
    if (this.isPendingRemoval(lang)) {
      this.undoRemoval(lang);
      return;
    }

    const opening = this.openLanguage !== lang;

    if (opening) {
      const other = SemTranslationPillsComponent.openInstance;
      if (other && other !== this) {
        // Leaving one popover for another is a click-away, and click-away commits.
        other.commit();
      }
      SemTranslationPillsComponent.openInstance = this;
      this.openLanguage = lang;
      this.draftOriginal = this.valueFor(lang);
      this.draft = this.draftOriginal;
      this.decideDirection();
    } else {
      this.commit();
    }
  }

  /** Puts a pending-removal row back exactly as it was. */
  undoRemoval(lang: string): void {
    if (!this.isPendingRemoval(lang)) {
      return;
    }
    const value = this.pendingRemovals[lang];
    delete this.pendingRemovals[lang];
    const r = this.row(lang, true);
    if (r) {
      r.Value = value;
    }
    this.markDirty();
  }

  toggleShowEmpty(): void {
    this.showEmpty = !this.showEmpty;
    if (!this.showEmpty && this.openLanguage && !this.hasValue(this.openLanguage)) {
      // Calming the field while an empty slot is open would leave a popover attached to a pill that
      // is no longer drawn. Commit rather than close, so a draft typed into that slot is not lost.
      this.commit();
    }
  }

  /** Open upward when the popover would not fit under the field. */
  private decideDirection(): void {
    try {
      const rect = (this.host.nativeElement as HTMLElement).getBoundingClientRect();
      const needed = this.multiline ? 210 : 130; // popover height incl. source line, with slack
      this.flipUp = rect.bottom + needed > window.innerHeight && rect.top > needed;
    } catch {
      this.flipUp = false;
    }
  }

  /** Typing touches the draft ONLY. Nothing reaches the record until commit. */
  onDraftInput(value: string): void {
    this.draft = value == null ? "" : value;
  }

  /**
   * Accepts the draft into the record and closes. An emptied draft is a removal, not an empty string:
   * the two are different instructions to the writer, and storing "" would leave an invisible row.
   */
  commit(): void {
    const lang = this.openLanguage;
    if (!lang) {
      return;
    }

    const value = this.draft || "";
    const changed = value !== this.draftOriginal;

    if (changed) {
      if (value.trim().length === 0) {
        this.removeTranslation(lang, /* fromCommit */ true);
      } else {
        const r = this.row(lang, true);
        if (r) {
          r.Value = value;
        }
        // Re-typing a value into a slot that was pending removal is a reinstatement.
        delete this.pendingRemovals[lang];
        this.markDirty();
      }
    }

    this.close();
  }

  /** Discards the draft and closes, leaving the record exactly as the popover found it. */
  cancel(): void {
    this.draft = this.draftOriginal;
    this.close();
  }

  /**
   * Removes the translation. This SPLICES the row out of the carrier rather than blanking its value,
   * because the two are different instructions to the writer: a row present with an empty value is
   * stored as an empty string, while a row absent from a posted list is deleted. Storing "" would
   * leave a permanent empty record nobody can see and nobody can clear.
   *
   * The carrier arrives from the server fully hydrated with every stored row, so removing ONE row
   * deletes exactly that one and leaves the rest untouched.
   */
  removeTranslation(lang: string, fromCommit: boolean = false): void {
    const rows = this.carrier(false);
    if (!rows) {
      return;
    }

    const idx = rows.findIndex(
      (r) => r && r.FieldName === this.fieldName && (r.LanguageCode || "").toLowerCase() === lang
    );
    if (idx >= 0) {
      // Remember what went, so the pill can keep showing it struck through until the save lands and
      // so the removal stays undoable.
      const removed = rows[idx].Value || "";
      const original = this.draftOriginal && this.openLanguage === lang ? this.draftOriginal : removed;
      rows.splice(idx, 1);
      this.pendingRemovals[lang] = original || removed;
      this.markDirty();
    }

    if (!fromCommit) {
      this.close();
    }
  }

  /** True once there is something to remove — an empty slot has no delete affordance. */
  canRemove(lang: string): boolean {
    return !this.disabled && this.hasValue(lang);
  }

  /**
   * Asks our own model on arch-ml for a draft, through the server (the Cloudflare Access token must
   * never reach the browser). ADVISORY: it fills the editor and stops. The curator still saves, and
   * an unreachable model simply produces nothing rather than an error over a convenience.
   */
  suggest(lang: string): void {
    if (!this.suggestAvailable || this.disabled || this.suggesting) {
      return;
    }
    const source = (this.sourceValue || "").trim();
    if (!source) {
      return;
    }

    this.suggesting = lang;

    const model = new RequestModel();
    model.contentType = "application/json";
    model.responseType = "application/json";
    model.url = "api/translation/suggest";
    model.data = {
      FieldName: this.fieldName,
      LanguageCode: lang,
      Text: source,
      FieldLabel: this.fieldLabel,
    };

    this.rest
      .getDataPOST<any>(model)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        this.suggesting = null;
        const value = res && res.Value ? String(res.Value) : "";
        // A suggestion is a proposal, so it lands in the draft like anything typed — reviewable,
        // and thrown away by Esc if it is no good.
        if (value && this.openLanguage === lang) {
          this.draft = this.maxLength > 0 ? value.substring(0, this.maxLength) : value;
        }
      });
  }

  private markDirty(): void {
    // Mark the form dirty so Save becomes available on a translation-only edit — otherwise a curator
    // types a German title, sees it accepted, and the Save button never lights up.
    if (this.dfms.globalForm) {
      this.dfms.globalForm.form.markAsDirty();
    }
  }

  /**
   * Esc CANCELS. It previously closed while keeping the edit, which meant the one gesture everybody
   * reaches for to get out of something they did not mean to do was the gesture that committed it,
   * and there was no way back at all.
   */
  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.openLanguage) {
      this.cancel();
    }
  }

  /** Clicking anywhere else on the form closes the popover, which is what a popover is expected to do. */
  @HostListener("document:mousedown", ["$event"])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.openLanguage) {
      return;
    }
    try {
      const el = this.host.nativeElement as HTMLElement;
      if (event && event.target && !el.contains(event.target as Node)) {
        // Click-away COMMITS, matching every other field on this form: clicking off an input does not
        // throw the typing away. Esc is the one gesture that discards, and it is the only one.
        this.commit();
      }
    } catch {
      /* a failed containment test must not trap the popover open */
    }
  }

  private close(): void {
    this.openLanguage = null;
    this.draft = "";
    this.draftOriginal = "";
    this.suggesting = null;
    if (SemTranslationPillsComponent.openInstance === this) {
      SemTranslationPillsComponent.openInstance = null;
    }
  }
}
